import { BaseViewModel, useViewModelConstructor } from '@/common/utils/mobx/ViewModel';
import { ClassNames } from '@emotion/react';
import {
  Dialog,
  DialogProps,
  FlexColumn,
  fullWidth,
  Button,
  Input,
  colors,
  IconButton,
} from '@toilet-paper-games/ui';
import { observer } from 'mobx-react-lite';
import * as Sentry from '@sentry/react';
import { action } from 'mobx';
import { makeSimpleAutoObservable } from '@/common/utils/mobx/mobx';
import { Send } from 'lucide-react';

const FeedbackType = {
  None: 'none',
  Bug: 'bug',
  Feature: 'feature',
  Feedback: 'feedback',
} as const;

class FeedbackDialogViewModel extends BaseViewModel<FeedbackDialogProps> {
  data = {
    name: '',
    email: '',
    message: '',
    type: null as (typeof FeedbackType)[keyof typeof FeedbackType] | null,
  };

  constructor(props: FeedbackDialogProps) {
    super(props);
    makeSimpleAutoObservable(this, {}, { autoBind: true });
  }

  get isValid() {
    const hasFilledMessage = this.data.message.length > 0;

    return hasFilledMessage;
  }

  submitFeedback() {
    Sentry.captureFeedback({
      message: this.data.message,
      name: this.data.name || undefined,
      email: this.data.email || undefined,
      tags: { feedback_type: this.data.type },
      url: window.location.href,
    });

    this.props.onOpenChange?.(false);
  }
}

const FeedbackDialogContent = observer((props: { vm: FeedbackDialogViewModel }) => {
  const { vm } = props;

  return (
    <form
      css={[fullWidth]}
      onSubmit={(e) => {
        e.preventDefault();
        vm.submitFeedback();
      }}
    >
      <FlexColumn gap={25} css={[fullWidth]}>
        <select
          css={{
            color: '#FFF',
            border: `2px solid ${colors.border}`,
            borderRadius: 10,
            background: colors.componentInset,
            fontSize: '1.6em',
            paddingLeft: 10,
            paddingRight: 10,
            paddingTop: 5,
            paddingBottom: 5,
            boxShadow:
              'inset 0 4px 0px rgba(0, 0, 0, 0.10), inset 0 4px 2px rgba(0, 0, 0, 0.23)',
            '&:focus': { outline: 'none', borderColor: colors.borderFocused },
            // change background of select dropdown
            '&>option': {
              background: colors.componentInsetSolid,
              border: `2px solid ${colors.border}`,
            },
          }}
          value={vm.data.type || ''}
          onChange={action(
            (e) =>
              (vm.data.type = e.target
                .value as (typeof FeedbackType)[keyof typeof FeedbackType]),
          )}
          required
        >
          <option value={FeedbackType.None}>Select type</option>
          <option value={FeedbackType.Bug}>Bug Report</option>
          <option value={FeedbackType.Feature}>Feature Request</option>
          <option value={FeedbackType.Feedback}>General Feedback</option>
        </select>

        <Input
          placeholder="Name (optional)"
          value={vm.data.name}
          onChange={action((e) => (vm.data.name = e.target.value))}
        />

        <Input
          placeholder="Email (optional)"
          type="email"
          value={vm.data.email}
          onChange={action((e) => (vm.data.email = e.target.value))}
        />

        <textarea
          placeholder="Message"
          css={{
            resize: 'vertical',
            color: '#FFF',
            border: `2px solid ${colors.border}`,
            borderRadius: 10,
            background: colors.componentInset,
            fontSize: '1.6em',
            paddingLeft: 10,
            paddingRight: 10,
            paddingTop: 5,
            paddingBottom: 5,
            boxShadow:
              'inset 0 4px 0px rgba(0, 0, 0, 0.10), inset 0 4px 2px rgba(0, 0, 0, 0.23)',
            '&:focus': { outline: 'none', borderColor: colors.borderFocused },
            // change border color of textarea if required is not met
            '&:invalid': { borderColor: colors.dangerBorder },
          }}
          value={vm.data.message}
          onChange={action((e) => (vm.data.message = e.target.value))}
          required
          rows={4}
        />

        <IconButton
          icon={<Send />}
          type="submit"
          disabled={!vm.isValid}
          css={{ marginTop: 20 }}
        >
          Submit Feedback
        </IconButton>
      </FlexColumn>
    </form>
  );
});

export interface FeedbackDialogProps extends DialogProps {}

export const FeedbackDialog = observer((props: FeedbackDialogProps) => {
  const { ...rest } = props;

  const vm = useViewModelConstructor(FeedbackDialogViewModel, props);

  return (
    <ClassNames>
      {({ css }) => (
        <Dialog
          {...rest}
          title="Submit Feedback"
          cardProps={{
            className: css({
              width: 'min(calc(100% - 20px), 500px)',
              padding: '20px',
            }),
          }}
        >
          <FeedbackDialogContent vm={vm} />
        </Dialog>
      )}
    </ClassNames>
  );
});
